import type { FC } from 'react';

import { DELETE_ACCOUNT, RETENTION_YEARS } from '@/features/legal/constants/deleteAccount.constants';
import { useDeleteAccountPage } from '@/features/legal/hooks/useDeleteAccountPage.hook';

const fieldClass =
  'mt-2 w-full rounded-xl border border-[var(--color-divider)] bg-white px-4 py-3 text-[15px] text-[var(--color-ink)] outline-none transition-colors focus:border-brand-600';

const primaryButtonClass =
  'mt-5 w-full rounded-xl bg-brand-600 px-5 py-3 text-[15px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto';

const DeleteAccountPage: FC = () => {
  const {
    step,
    email,
    code,
    emailError,
    codeError,
    submitError,
    isSubmitting,
    onChangeEmail,
    onChangeCode,
    onSubmitEmail,
    onSubmitCode,
    onBack,
  } = useDeleteAccountPage();

  return (
    <article className="bg-white">
      <div className="mx-auto w-full max-w-3xl px-5 py-16 md:px-8 md:py-20">
        <header>
          <p className="text-xs font-semibold tracking-[0.14em] text-brand-600 uppercase">
            {DELETE_ACCOUNT.eyebrow}
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-[var(--color-ink)] md:text-5xl">
            {DELETE_ACCOUNT.title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-[var(--color-ink-muted)]">
            {DELETE_ACCOUNT.summary}
          </p>
        </header>

        <section className="mt-10 rounded-2xl border border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-6 md:p-8">
          {step === 'email' && (
            <form onSubmit={onSubmitEmail} noValidate>
              <label
                htmlFor="deletion-email"
                className="text-sm font-semibold text-[var(--color-ink)]"
              >
                {DELETE_ACCOUNT.emailLabel}
              </label>
              <input
                id="deletion-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={onChangeEmail}
                placeholder={DELETE_ACCOUNT.emailPlaceholder}
                aria-invalid={emailError !== null}
                aria-describedby={emailError ? 'deletion-email-error' : undefined}
                className={fieldClass}
              />
              {emailError && (
                <p id="deletion-email-error" className="mt-2 text-sm text-red-600">
                  {emailError}
                </p>
              )}
              {submitError && <p className="mt-2 text-sm text-red-600">{submitError}</p>}
              <button type="submit" disabled={isSubmitting} className={primaryButtonClass}>
                {isSubmitting ? 'Sending…' : DELETE_ACCOUNT.emailCta}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={onSubmitCode} noValidate>
              <h2 className="font-display text-xl font-bold text-[var(--color-ink)]">
                {DELETE_ACCOUNT.codeHeading}
              </h2>
              <p className="mt-2 text-[15px] leading-relaxed text-[var(--color-ink-muted)]">
                {DELETE_ACCOUNT.codeIntro}
              </p>

              <label
                htmlFor="deletion-code"
                className="mt-6 block text-sm font-semibold text-[var(--color-ink)]"
              >
                {DELETE_ACCOUNT.codeLabel}
              </label>
              <input
                id="deletion-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={onChangeCode}
                placeholder={DELETE_ACCOUNT.codePlaceholder}
                aria-invalid={codeError !== null}
                aria-describedby={codeError ? 'deletion-code-error' : undefined}
                className={`${fieldClass} max-w-[160px] tracking-[0.4em]`}
              />
              {codeError && (
                <p id="deletion-code-error" className="mt-2 text-sm text-red-600">
                  {codeError}
                </p>
              )}
              {submitError && <p className="mt-2 text-sm text-red-600">{submitError}</p>}

              <div className="flex flex-col items-start gap-3 md:flex-row md:items-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`${primaryButtonClass} !bg-red-600`}
                >
                  {isSubmitting ? 'Deleting…' : DELETE_ACCOUNT.codeCta}
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="text-sm font-semibold text-[var(--color-ink-muted)] underline-offset-4 hover:underline md:mt-5"
                >
                  {DELETE_ACCOUNT.codeBack}
                </button>
              </div>
            </form>
          )}

          {step === 'done' && (
            <div>
              <h2 className="font-display text-xl font-bold text-[var(--color-ink)]">
                {DELETE_ACCOUNT.doneHeading}
              </h2>
              <p className="mt-2 text-[15px] leading-relaxed text-[var(--color-ink-muted)]">
                {DELETE_ACCOUNT.doneBody}
              </p>
            </div>
          )}
        </section>

        <div className="mt-12 flex flex-col gap-10">
          <section>
            <h2 className="font-display text-xl font-bold text-[var(--color-ink)]">
              {DELETE_ACCOUNT.removedHeading}
            </h2>
            <ul className="mt-4 flex list-disc flex-col gap-2 pl-5">
              {DELETE_ACCOUNT.removed.map((item) => (
                <li key={item} className="text-[15px] leading-relaxed text-[var(--color-ink-muted)]">
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-[var(--color-ink)]">
              {DELETE_ACCOUNT.keptHeading}
            </h2>
            {DELETE_ACCOUNT.kept.map((paragraph) => (
              <p
                key={paragraph}
                className="mt-4 text-[15px] leading-relaxed text-[var(--color-ink-muted)]"
              >
                {paragraph}
              </p>
            ))}
            <p className="mt-4 text-[15px] leading-relaxed text-[var(--color-ink-muted)]">
              Retention period: <strong>{RETENTION_YEARS} years</strong> from the date of deletion.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-[var(--color-ink)]">
              {DELETE_ACCOUNT.blockedHeading}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[var(--color-ink-muted)]">
              {DELETE_ACCOUNT.blockedBody}
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-[var(--color-ink)]">
              {DELETE_ACCOUNT.helpHeading}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[var(--color-ink-muted)]">
              {DELETE_ACCOUNT.helpBody}
            </p>
          </section>
        </div>
      </div>
    </article>
  );
};

export default DeleteAccountPage;
